import { formValidation } from "@form-validation/bundle/popular";

// Locales
import { en_US } from "@form-validation/locales/en_US";
import { fr_FR } from "@form-validation/locales/fr_FR";

// Locales override
import { fr_FR as fr_override } from "./lang/fr_FR";
import { en_US as en_override } from "./lang/en_US";

// Plugins
import { SubmitButton } from "@form-validation/plugin-submit-button";
import { DefaultSubmit } from "@form-validation/plugin-default-submit";
import { Aria } from "@form-validation/plugin-aria";
import { Trigger } from "@form-validation/plugin-trigger";
import { Declarative } from "@form-validation/plugin-declarative";
import { Transformer } from "@form-validation/plugin-transformer";
// import { InternationalTelephoneInput } from "@form-validation/plugin-international-telephone-input";
import { Excluded } from "@form-validation/plugin-excluded";

// Validateurs
import { zipCode } from "@form-validation/validator-zip-code";
import { phone } from "@form-validation/validator-phone";

// Custom
import { findAssociatedLabelOrLegend } from "./label-utils";
import { CustomMessage } from "./plugins/CustomMessage";

// Not utilisé pour le moment
// import intlTelInput from "intl-tel-input";
// import "intl-tel-input/build/css/intlTelInput.css";

export default class FormValidate {
  constructor(form) {
    this.form = form;
    this.server_path =
      "https://sefco.solutions.hydroquebec.com/kf/SefcoServlet";

    this.lang = document.documentElement.lang || "fr";
    /* istanbul ignore next */ this.trackData =
      this.form.getAttribute("data-analytics") === "formulaire" ? true : false;
    this.messages = this.form.querySelector(".hq-messages-erreurs");

    // if (typeof window !== "undefined") {
    //   window.intlTelInput = intlTelInput;
    // }

    this.fr_FR = this.deepMerge(structuredClone(fr_FR), fr_override);
    this.en_US = this.deepMerge(structuredClone(en_US), en_override);

    // Variables
    this.describedByAria = "aria-describedby";
    this.dataOriginDescribedBy = "data-origin-described-by";
    this.messageErreurClass = "message-erreur";
    this.wrapFileClass = "js-wrap-file";

    this.fv = formValidation(this.form, {
      framework: "hq",
      live: "disabled",
      autofocus: false,
      trigger: null,

      locale: this.lang,
      localization: this.lang === "en" ? this.en_US : this.fr_FR,

      fields: this.addFields(),
      plugins: this.addPlugins()
    });

    this.init();
  }

  /**
   * Initialise la validation du formulaire en simplifiant les messages personnalisés et en ajoutant des event listeners.
   */
  init() {
    this.form.setAttribute("action", this.server_path);
    this.fv.registerValidator("zipCode", zipCode);
    this.fv.registerValidator("phone", phone);

    // On met les `aria-describedby` dans des attributs `data` pour être réutilisé.
    const fields = this.fv.getFields();
    Object.keys(fields).forEach(field => {
      const elements = this.fv.getElements(field);
      elements.forEach(element => {
        if (element.hasAttribute(this.describedByAria)) {
          const attr = element.getAttribute(this.describedByAria);
          element.dataset.originDescribedBy = attr;
        }

        if (element.type === "radio" && element.closest("fieldset")) {
          const fieldset = element.closest("fieldset");

          if (fieldset.hasAttribute(this.describedByAria)) {
            const attr = fieldset.getAttribute(this.describedByAria);
            fieldset.dataset.originDescribedBy = attr;
          }
        }
      });
    });

    this.simplifyCustomMessages();
    this.addEventListeners();
  }

  deepMerge(target, source) {
    for (const key in source) {
      if (source[key] instanceof Object && key in target) {
        target[key] = this.deepMerge(structuredClone(target[key]), source[key]);
      } else {
        target[key] = source[key];
      }
    }
    return target;
  }

  /**
   * Ajoute les configurations des champs pour la validation.
   * Redéfinir cette méthode pour spécifier les règles de validation des champs.
   *
   * @returns {Object} Un objet contenant les configurations de validation des champs.
   */
  addFields() {
    return {
      fv_code_postal: {
        selector: 'input[name="code_postal"],input[name="code-postal"]',
        validators: {
          zipCode: {}
        }
      },
      telephone_required: {
        selector: 'input[name="cellulaire"], input[name="telephone"]',
        validators: {
          phone: {}
        }
      },
      fv_numero_client: {
        selector: 'input[name="numero_client"]',
        validators: {
          regexp: {}
        }
      }
    };
  }

  /**
   * Ajoute les plugins pour la validation du formulaire.
   *
   * @returns {Object} Un objet contenant les configurations des plugins.
   */
  addPlugins() {
    return {
      message: new CustomMessage({
        container: ".hq-messages-erreurs ul"
      }),
      submitButton: new SubmitButton(),
      declarative: new Declarative({
        html5Input: true
      }),
      aria: new Aria(),
      excluded: new Excluded({}),

      transformer: new Transformer({
        fv_code_postal: {
          zipCode: function(field, element) {
            const value = element.value;

            //On met tout en majuscule, on retire les espaces et on met un espace au milieu
            const cp = value
              .toUpperCase()
              .replace(/\s/g, "")
              .replace(/^([A-Z]\d[A-Z])(\d[A-Z]\d)$/, "$1 $2");

            element.value = cp; // Met à jour la valeur de l'élément
            return cp;
          }
        },
        telephone_required: {
          phone: function(field, element) {
            const value = element.value;

            const tel = value.replace(
              /^(\d{3})[\-\s]?(\d{3})[\-\s]?(\d{4})$/,
              "$1-$2-$3"
            ); // Remplace les espaces par des tirets

            element.value = tel; // Met à jour la valeur de l'élément
            return tel;
          }
        },
        fv_numero_client: {
          regexp: function(field, element) {
            const value = element.value;

            const numClient = value.replace(
              /^(1[0-9]{2})\s?([0-9]{3})\s?([0-9]{3})$/,
              "$1 $2 $3"
            );
            element.value = numClient; // Met à jour la valeur de l'élément
            return numClient;
          }
        }
      })
    };
  }

  /**
   * Ajoute dynamiquement un champ à la validation du formulaire.
   *
   * @param {string} name - Le nom du champ.
   * @param {Object} opts - Les options pour la validation du champ.
   */
  addField(name, opts = {}) {
    this.fv.addField(name, opts);
  }
  /**
   * Supprime un champ de la validation du formulaire.
   *
   * @param {string} name - Le nom du champ à supprimer.
   */
  removeField(name) {
    this.fv.removeField(name);
  }

  /**
   * Ajoute des event listeners pour la validation et l'interaction du formulaire.
   */
  addEventListeners() {
    this.form.addEventListener("submit", e => {
      e.preventDefault();

      // TODO: décommenter pour debugger
      // const formData = new FormData(this.form);
      // console.log(
      //   "Form data submitted:",
      //   Object.fromEntries(formData.entries())
      // );

      this.form.submit();
    });

    this.fv.on("core.form.valid", e => {
      this.form.classList.remove("is-validating");
      this.form.dispatchEvent(
        new Event("submit", { bubbles: true, cancelable: true })
      );
    });

    this.fv.on("core.form.invalid", e => {
      this.form.querySelector(".hq-messages-erreurs").focus();
    });

    this.fv.on("core.form.validating", e => {
      this.form.classList.add("is-validating");
    });

    this.form.addEventListener("reset", e => {
      // this.fv.resetForm(true);
      const fields = this.fv.getFields();

      Object.keys(fields).forEach(field => {
        const elements = this.fv.getElements(field);

        if (elements.length > 0 && elements.every(el => el != null)) {
          this.fv.resetField(field, true);
        }
      });
      this.fv.emit("core.form.reset", {});

      this.form.classList.remove("is-validating");
      this.form.scrollIntoView({
        behavior: "auto",
        block: "start"
      });
    });
    this.fv.on("core.form.reset", e => {
      const fields = this.fv.getFields();
      Object.keys(fields).forEach(field => {
        const elements = this.fv.getElements(field);
        elements.forEach(element => {
          // Add null check before accessing element properties
          if (element && typeof element.removeAttribute === "function") {
            this.removeErrorMessages(element, elements.length);
            element.removeAttribute("aria-invalid");
            element.removeAttribute(this.describedByAria);

            if (element.hasAttribute(this.dataOriginDescribedBy)) {
              const originDescribedBy = element.dataset.originDescribedBy;
              element.setAttribute(this.describedByAria, originDescribedBy);
            }

            const fieldset = element.closest("fieldset");
            if ("radio" === element.type && fieldset) {
              fieldset.removeAttribute(this.describedByAria);
              if (fieldset.hasAttribute(this.dataOriginDescribedBy)) {
                const originDescribedBy = fieldset.dataset.originDescribedBy;
                fieldset.setAttribute(
                  this.describedByAria,
                  `${originDescribedBy}`
                );
              }
            }

            if (element.type === "file") {
              const el = element.closest(`.${this.wrapFileClass}`);
              inputFileModule.resetInputFile(el);
            }
          }
        });
      });

      const adresse = this.form.querySelector("#adresse-manuel");
      if (adresse) {
        const addressResultContainer = document.querySelector(
          ".js-address-result-container"
        );
        addressResultContainer.classList.toggle("hidden", true);
        document.querySelector(".js-address-result").innerHTML = "";
        adresse.checked = true;
        adresse.click();
      }
    });

    // Utilise la délégation d'événements pour gérer les clics sur les liens ajoutés dynamiquement
    this.messages.querySelector("ul.hq-liste").addEventListener("click", e => {
      if (e.target.tagName === "A") {
        e.preventDefault();
        // Récupère l'ID de l'élément cible à partir du href
        const targetId = e.target.getAttribute("href").substring(1);
        const targetElement = document.getElementById(targetId);

        if (targetElement) {
          // Met le focus sur l'élément cible sans changer l'URL
          targetElement.scrollIntoView({
            behavior: "smooth",
            block: "center"
          });
          targetElement.focus({ preventScroll: true });
        }
      }
    });

    /**
     * Plus tard...
     */
    // if (!!this.trackData) {
    //   const buttons = this.form.querySelectorAll("button");
    //   buttons.forEach(button => {
    //     button.addEventListener("click", this.handleButtonTrack.bind(this));
    //   });
    // }

    this.fv.on("core.element.validating", e => {
      this.removeErrorMessages(e.element, e.elements.length);
    });

    this.fv
      .on("core.validator.validated", e => {
        if (e.validator !== "file" && !e.result.valid) {
          this.placeErrorMessage(
            e.element,
            e.result.message,
            e.elements.length
          );
        }
      })
      .on("core.validator.validated", e => {
        if (e.validator === "file") {
          const el = e.element.closest(`.${this.wrapFileClass}`);

          if (!e.result.valid) {
            inputFileModule.displayError(
              el,
              this.fv.localization.file[e.result.meta.error]
            );
          } else {
            inputFileModule.displaySuccess(
              el,
              this.fv.localization.file.success
            );
            if (e.element.files.length < 1) {
              el.setAttribute("role", "button");
              el.setAttribute("tabindex", 0);
            } else {
              el.removeAttribute(this.describedByAria);
            }
          }
        }
      });

    this.fv.on("plugins.message.displayed", e => {
      const describedBy = e.element.getAttribute(this.describedByAria);
      if (e.element.hasAttribute(this.dataOriginDescribedBy)) {
        const originDescribedBy = e.element.dataset.originDescribedBy;
        e.element.setAttribute(
          this.describedByAria,
          `${describedBy} ${originDescribedBy}`
        );
      }

      const allElements = this.fv.getElements(e.field);
      const atributeType = e.element.getAttribute("type");
      if ("checkbox" === atributeType) {
        allElements.forEach(ele => {
          if (ele.hasAttribute(this.dataOriginDescribedBy)) {
            const originDescribedBy = ele.dataset.originDescribedBy;
            ele.setAttribute(
              this.describedByAria,
              `${describedBy} ${originDescribedBy}`
            );
          }
        });
      } else if ("radio" === atributeType) {
        const fieldset = e.element.closest("fieldset");
        fieldset.setAttribute(this.describedByAria, `${describedBy}`);
        if (fieldset.hasAttribute(this.dataOriginDescribedBy)) {
          const originDescribedBy = fieldset.dataset.originDescribedBy;
          fieldset.setAttribute(
            this.describedByAria,
            `${describedBy} ${originDescribedBy}`
          );
        }

        allElements.forEach(ele => {
          ele.removeAttribute(this.describedByAria);

          if (ele.hasAttribute(this.dataOriginDescribedBy)) {
            const originDescribedBy = ele.dataset.originDescribedBy;
            ele.setAttribute(this.describedByAria, `${originDescribedBy}`);
          }
        });
      }
    });
    this.fv.on(
      "plugins.message.displayed",
      this.changeErrorContainer.bind(this)
    );

    this.fv
      .on("core.field.valid", field => {
        const elements = this.fv.getElements(field);

        elements.forEach(ele => {
          if (ele.hasAttribute(this.dataOriginDescribedBy)) {
            const originDescribedBy = ele.dataset.originDescribedBy;
            ele.setAttribute(this.describedByAria, originDescribedBy);
          }
        });
      })
      .on("core.element.validated", e => {
        const element = e.element;
        const isValid = e.valid;
        if (isValid && element.hasAttribute(this.dataOriginDescribedBy)) {
          const originDescribedBy = element.dataset.originDescribedBy;
          element.setAttribute(this.describedByAria, originDescribedBy);
        }

        const fieldset = element.closest("fieldset");
        if (isValid && "radio" === element.type) {
          fieldset.removeAttribute(this.describedByAria);
          if (fieldset.hasAttribute(this.dataOriginDescribedBy)) {
            const originDescribedBy = fieldset.dataset.originDescribedBy;
            fieldset.setAttribute(this.describedByAria, `${originDescribedBy}`);
          }
        }
      });
  }

  /**
   * Supprime les messages d'erreur associés à un élément de champ.
   *
   * @param {HTMLElement} element - L'élément du champ.
   * @param {number} length - Le nombre d'éléments dans le groupe de champs.
   */
  removeErrorMessages(element, length) {
    const inputType = element.type;
    const tagName = element.tagName.toLowerCase();

    if ((inputType === "radio" || inputType === "checkbox") && length > 1) {
      const fieldset = element.closest("fieldset");
      if (fieldset) {
        const errorMessage = fieldset.querySelector(
          `.${this.messageErreurClass}`
        );
        if (errorMessage) {
          errorMessage.remove();
        }
      }
    } else if (inputType === "file") {
      const wrapper = element.closest(`.${this.wrapFileClass}`);
      if (wrapper) {
        const errorMessage = wrapper.parentNode.querySelector(
          `.${this.messageErreurClass}`
        );
        errorMessage?.remove();
      }
    } else {
      const errorMessage =
        inputType === "checkbox" && length === 1
          ? element.parentNode.nextElementSibling
          : element.nextElementSibling;
      if (
        errorMessage &&
        errorMessage.classList.contains(this.messageErreurClass)
      ) {
        errorMessage.remove();
      }
    }
  }

  /**
   * Place un message d'erreur près de l'élément du champ.
   *
   * @param {HTMLElement} element - L'élément du champ.
   * @param {string} message - Le message d'erreur à afficher.
   * @param {number} length - Le nombre d'éléments dans le groupe de champs.
   */
  placeErrorMessage(element, message, length) {
    const clonedMessage = document.createElement("p");
    clonedMessage.classList.add(this.messageErreurClass);
    clonedMessage.setAttribute("tabindex", "-1");

    const existingSvg = this.messages?.querySelector("svg");
    if (existingSvg) {
      const svgIcon = existingSvg.cloneNode(true);
      svgIcon.setAttribute("class", "hq-icone-medium");
      clonedMessage.appendChild(svgIcon);
    }

    const spanElement = document.createElement("span");
    spanElement.innerHTML = message;
    clonedMessage.appendChild(spanElement);

    const inputType = element.type;
    const tagName = element.tagName.toLowerCase();

    if ((inputType === "radio" || inputType === "checkbox") && length > 1) {
      const fieldset = element.closest("fieldset");
      if (fieldset) {
        const legend = fieldset.querySelector("legend");
        const texteAide = fieldset.querySelector(".texte-aide");
        if (legend) {
          texteAide
            ? fieldset.insertBefore(clonedMessage, texteAide)
            : legend.insertAdjacentElement("afterend", clonedMessage);
        }
      }
    } else if (inputType === "file") {
      const wrapper = element.closest(`.${this.wrapFileClass}`);
      wrapper?.insertAdjacentElement("afterend", clonedMessage);
    } else {
      const targetElement =
        inputType === "checkbox" && length === 1 ? element.parentNode : element;
      targetElement.insertAdjacentElement("afterend", clonedMessage);
    }
  }

  /**
   * Modifie le conteneur des messages d'erreur pour améliorer l'accessibilité et l'utilisabilité.
   *
   * @param {Object} e - L'objet événement contenant les détails du message et de l'élément.
   */
  changeErrorContainer(e) {
    const messageElement = e.messageElement;
    if (messageElement) {
      // Retirer l'attribut "role" pour éviter les conflits d'accessibilité
      messageElement.removeAttribute("role");

      const associatedLabel = findAssociatedLabelOrLegend(e.element);
      if (associatedLabel) {
        if (e.element.type === "file" && e.meta) {
          e.message = this.fv.localization.file[e.meta.error];
        }
        messageElement.innerHTML = `${associatedLabel}: ${e.message}`;
      }

      if (messageElement.parentNode) {
        const item = document.createElement("a");
        Array.from(messageElement.attributes).forEach(attr => {
          item.setAttribute(attr.name, attr.value);
        });
        item.innerHTML = messageElement.innerHTML;
        let inputLink = `#${e.element.getAttribute("id")}`;

        if (e.element.type === "file") {
          const fieldset = e.element.closest("fieldset");
          const id = `js-fv-${e.field}-${Date.now()}-label`;

          inputLink = `#${id}`;
          fieldset
            ?.querySelector(`.${this.wrapFileClass}`)
            ?.setAttribute("id", id);
          fieldset
            ?.querySelector(`.${this.wrapFileClass}`)
            ?.setAttribute("role", "button");
        }

        item.setAttribute("href", inputLink);
        messageElement.parentNode.replaceChild(item, messageElement);
      }
    }
  }

  /**
   * Suit les interactions des boutons à des fins d'analyse.
   *
   * @param {Event} event - L'événement de clic déclenché par le bouton.
   */
  /* istanbul ignore next */ handleButtonTrack(event) {
    const button = event.currentTarget;
    this.sendDataToGA4(button);
  }

  /**
   * Envoie les données d'interaction à Google Analytics 4 (GA4).
   *
   * @param {HTMLButtonElement} button - L'élément bouton qui a déclenché l'interaction.
   */
  /* istanbul ignore next */ sendDataToGA4(button) {
    const gaEvent = {
      event: "user_interaction",
      event_name: "interaction_formulaire",
      interaction: {
        interaction_element_type: "bouton",
        interaction_element_label: undefined,
        interaction_type: undefined,
        lead_step_nb: undefined,
        lead_step_name: undefined,
        page_subsection: undefined
      }
    };

    const elementType = button.getAttribute("type");
    const interaction_element_label = button.innerText.trim();

    gaEvent.interaction.page_subsection = button
      .closest("section")
      .querySelector("h2")
      .innerText.trim();

    // console.log(elementType, interaction_element_label, gaEvent);

    switch (elementType) {
      case "submit":
        gaEvent.event_name = "completion_formulaire";
        gaEvent.interaction.interaction_element_label = interaction_element_label;
        gaEvent.interaction.interaction_type = "soumission-formulaire";
        break;

      default:
        let currentSection = button.closest("section");
        let currentStep;
        let currentStepName;

        if (currentSection) {
          currentStep = currentSection.dataset.step;
          currentStepName = currentSection.querySelector("h2").innerText.trim();
        }

        gaEvent.interaction.interaction_element_label = interaction_element_label;
        gaEvent.interaction.interaction_type = "acces-contenu";
        gaEvent.interaction.lead_step_nb = currentStep.toString();
        gaEvent.interaction.lead_step_name = currentStepName;

        /*
        var fv = $("#sefcoForm").data("formValidation");
        var container = $("#sefcoForm").find(
          'section[data-step="' + currentStep + '"]'
        );

        fv.validateContainer(container);
        var isValidStep = fv.isValidContainer(container);

        if (isValidStep) {
          dataLayer.push(gaEvent);
        } else if (button.dataset.stepPrev !== undefined) {
          dataLayer.push(gaEvent);
        }
*/
        break;
    }

    /*
    $('#sefcoForm[data-analytics="formulaire"]')
      .on("success.form.fv", function() {
        dataLayer.push(gaEvent);
        this.resetDataLayer();
      })
      .on("err.form.fv", function() {
        //! à décommenter uniquement pour les tests !
        if (window.location.host != "www.hydroquebec.com") {
          console.log("gaEvent", gaEvent);
          dataLayer.push(gaEvent);
          this.resetDataLayer();
        }
      });
    */
  }

  /**
   * Réinitialise le dataLayer pour le suivi analytique.
   */
  /* istanbul ignore next */ resetDataLayer() {
    const resEvent = {
      event: "reset_dataLayer",
      event_name: "reset_dataLayer",
      interaction: {
        interaction_element_type: undefined,
        interaction_element_label: undefined,
        interaction_type: undefined,
        interaction_behavior: undefined,
        page_section: undefined,
        page_subsection: undefined,
        lead_step_nb: undefined,
        lead_step_name: undefined
      }
    };
    dataLayer.push(resEvent);
  }

  /**
   * Simplifie les messages personnalisés des champs du formulaire en mettant à jour les options des validateurs.
   * Cette méthode garantit que les messages personnalisés sont appliqués de manière cohérente sur les champs.
   */
  simplifyCustomMessages() {
    const elementsWithCustomMessages = this.form.querySelectorAll(
      "[data-fv-message]"
    );

    elementsWithCustomMessages.forEach(element => {
      const customMessage = element.getAttribute("data-fv-message");
      const fieldName =
        element.getAttribute("name") || element.getAttribute("data-fv-field");

      if (fieldName && this.fv.getFields()[fieldName]) {
        // Update the field configuration
        this.fv.updateFieldStatus(fieldName, "NotValidated");

        // Get current validators and update their messages
        const field = this.fv.getFields()[fieldName];

        if (field && field.validators) {
          this.fv.updateValidatorOption(
            fieldName,
            "notEmpty",
            "message",
            customMessage
          );
        }
      }
    });
  }
}

/* istanbul ignore next */
document.addEventListener("DOMContentLoaded", () => {
  const forms = document.querySelectorAll(".js-hq-form, #sefcoForm");

  forms.forEach(form => {
    const formValidationInstance = new FormValidate(form);

    // Store in a Map for multiple forms support
    if (!window.formValidateInstances) {
      window.formValidateInstances = new Map();
    }

    // Store the instance using form ID as key for easier retrieval
    const formId =
      form.id ||
      `form-${Date.now()}-${Math.random()
        .toString(36)
        .substring(2, 9)}`;
    if (!form.id) {
      form.id = formId;
    }

    window.formValidateInstances.set(formId, formValidationInstance);
  });

  // Create global utility function if it doesn't exist
  if (!window.getFormValidator) {
    window.getFormValidator = function(formElement) {
      const formId = formElement.id;
      return window.formValidateInstances.get(formId);
    };
  }

  const form = document.querySelector(".js-hq-form, #sefcoForm");
  const validator = window.getFormValidator(form);

  // TODO: À titre d'exemple seulement, à retirer ou adapter selon les besoins réels.
  // validator?.addField("username", {
  //   selector:
  //     'input[name="installation_thermopompes_marche_residentiel"], input[name="adresse"]',
  //   validators: {
  //     notEmpty: {
  //       message: "Veuillez entrer un nom d'utilisateur valide"
  //     }
  //   }
  // });
});
